import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Pages that render their own focused chrome instead of the site navigation.
const CHROMELESS_PATHS = ['/oauth/authorize'];
// Sign-in pages reached from the consent screen get a logo-only header: no links to wander off on.
const AUTH_PATHS = ['/login', '/register'];
const trimPath = (p) => String(p || '').replace(/\/+$/, '') || '/';

const PAGE_LINKS = [
  { to: '/', label: 'Discover Papers' },
  { to: '/paper-rankings', label: 'Paper Rankings' },
  { to: '/about', label: 'About' },
  { to: '/developers', label: 'Developers' },
];
const DESKTOP_LINK = 'whitespace-nowrap font-medium text-sm text-gray-600 hover:text-gray-800 ml-4';
const MOBILE_LINK = 'block rounded-md px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900';

const Navigation = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);
  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/login');
  };

  // Close the mobile menu on navigation, Escape, or a tap outside it.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    const onPointer = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [menuOpen]);

  const path = trimPath(location.pathname);
  // The OAuth consent screen is a focused, single-decision page: no site navigation.
  if (CHROMELESS_PATHS.includes(path)) {
    return null;
  }
  if (AUTH_PATHS.includes(path) && CHROMELESS_PATHS.includes(trimPath(location.state?.from?.pathname))) {
    return (
      <header className="bg-white shadow-md">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center gap-6">
          <span className="flex items-center">
            <img src="/icon.png" alt="" className="h-10 w-auto" />
            <span className="whitespace-nowrap font-bold text-sm text-gray-800 ml-3">Everscope</span>
          </span>
          <span className="text-sm text-gray-600">Connecting an app</span>
        </div>
      </header>
    );
  }

  return (
    <nav ref={navRef} className="bg-white shadow-md relative z-40" aria-label="Main">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center gap-6 min-w-0">
        <div className="flex items-center">
          <Link to="/?" className="flex-shrink-0">
            <img
              src="/icon.png"
              alt="Everscope Logo"
              className="h-10 w-auto"
            />
          </Link>
          <Link to="/?" className="whitespace-nowrap font-bold text-sm text-gray-800 ml-3" key={`everscope-link-${Date.now()}`}>
            Everscope
          </Link>
          {/* Full link row only where it fits next to the account controls; below lg it lives in the menu. */}
          {PAGE_LINKS.map(({ to, label }) => (
            <Link key={to} to={to} className={`hidden lg:block ${DESKTOP_LINK}`}>{label}</Link>
          ))}
        </div>
        <div className="hidden lg:flex items-center shrink-0">
          {user ? (
            <>
              {/* Long emails used to push into the links; the full address lives on /account/developer. */}
              <span className="hidden 2xl:inline max-w-[14rem] truncate whitespace-nowrap text-gray-600 mr-4" title={user.sub}>
                Welcome, {user.sub}
              </span>
              <Link
                to="/account/developer"
                className="whitespace-nowrap text-sm font-medium text-gray-600 hover:text-gray-800 mr-4"
              >
                Developer settings
              </Link>
              <button
                onClick={handleLogout}
                className="whitespace-nowrap bg-blue-100 hover:bg-blue-200 text-blue-700 font-medium py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-sm hover:shadow"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-gray-600 hover:text-gray-800 mr-4">Login</Link>
              <Link to="/register" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">Sign Up</Link>
            </>
          )}
        </div>
        <button
          type="button"
          className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          aria-controls="mobile-menu"
          aria-expanded={menuOpen}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
        </button>
      </div>
      {menuOpen && (
        <div id="mobile-menu" className="lg:hidden absolute inset-x-0 top-full bg-white shadow-md border-t border-gray-100">
          <div className="container mx-auto px-4 sm:px-6 py-3 space-y-1">
            {PAGE_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} className={MOBILE_LINK} onClick={() => setMenuOpen(false)}>{label}</Link>
            ))}
          </div>
          <div className="container mx-auto px-4 sm:px-6 py-3 border-t border-gray-100">
            {user ? (
              <div className="space-y-1">
                <p className="px-3 pb-1 text-sm text-gray-500 truncate" title={user.sub}>Signed in as {user.sub}</p>
                <Link to="/account/developer" className={MOBILE_LINK} onClick={() => setMenuOpen(false)}>Developer settings</Link>
                <button type="button" onClick={handleLogout} className={`${MOBILE_LINK} w-full text-left`}>Logout</button>
              </div>
            ) : (
              <div className="flex gap-3 px-3 py-1">
                <Link to="/login" onClick={() => setMenuOpen(false)} className="flex-1 text-center rounded border border-gray-300 py-2 font-medium text-gray-700 hover:bg-gray-50">Login</Link>
                <Link to="/register" onClick={() => setMenuOpen(false)} className="flex-1 text-center rounded bg-blue-500 hover:bg-blue-700 py-2 font-bold text-white">Sign Up</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
